/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React from "react";
import styled from "styled-components";
import Icon from "../ui/Icon";
import Text from "../ui/Text";
import Title from "../ui/Title";
import Button from "../ui/Button";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../constants/constants";
import FormatDateTime from "../formatters/FormatDateTime";
import FormatDatePeriod from "../formatters/FormatDatePeriod";
import { useTranslate } from "../../hooks/useTranslate";

const StyledDateTimeCard = styled.div`
  display: grid;
  gap: 24px;
`;

const DateTimeInner = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const DateTimeCardIcon = styled.div`
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  border: 1px solid rgba(${({ theme }) => theme.colors.iconStroke.rgba});
`;

const DateTimeCardInfo = styled.div`
  display: grid;
  gap: 4px;
`;

const DateTimeCardControl = styled.div`
  margin-left: auto;
`;

const DateTimeRecurringDates = styled.div`
  display: grid;
  gap: 8px;
  padding: 16px;
  border: 1px solid rgba(${({ theme }) => theme.colors.border.rgba});
  border-radius: 4px;
`;

const DateTimeRecurringDate = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DateTimeCard = ({ date, timeFrom, timeTo, timeLabel, canEdit, canDelete, recurringData, onDelete, showIcon = true }) => {
  const { _t } = useTranslate();
  const navigate = useNavigate();

  return (
    <StyledDateTimeCard>
      <DateTimeInner>
        {showIcon && (
          <DateTimeCardIcon>
            <Icon.SelectDateTime size={Icon.SIZE.SM} />
          </DateTimeCardIcon>
        )}
        <DateTimeCardInfo>
          <Text>
            <FormatDateTime value={date} format={FormatDateTime.FORMAT.DATE} />
          </Text>
          <Title size="h3">
            {timeLabel}
            {!timeLabel && timeFrom && !timeTo && <FormatDateTime value={timeFrom} format={FormatDateTime.FORMAT.TIME} />}
            {!timeLabel && timeFrom && timeTo && (
              <FormatDatePeriod from={timeFrom} to={timeTo} format={FormatDatePeriod.FORMAT.TIME} />
            )}
          </Title>
        </DateTimeCardInfo>
        <DateTimeCardControl>
          {canDelete && (
            <Button
              variant="transparent"
              size="icon"
              icon={<Icon.Trash color="iconStroke" />}
              onClick={onDelete}
              aria-label={_t("Remove date and time")}
            />
          )}
          {canEdit && (
            <Button
              variant="transparent"
              size="icon"
              icon={<Icon.PencilAndPaper color="secondary" />}
              onClick={() => navigate(ROUTES.DATETIME)}
              aria-label={_t("Edit date and time")}
            />
          )}
        </DateTimeCardControl>
      </DateTimeInner>
      {recurringData && (
        <DateTimeRecurringDates>
          <Text color="dark" weight="bold">
            Recurring_date
          </Text>
          {recurringData?.dates.map((date) => (
            <DateTimeRecurringDate key={date}>
              <Text>
                <FormatDateTime value={date} format={FormatDateTime.FORMAT.DATE} />
              </Text>
              <Text color="dark">
                <FormatDatePeriod from={timeFrom} to={timeTo} format={FormatDatePeriod.FORMAT.TIME} />
              </Text>
            </DateTimeRecurringDate>
          ))}
        </DateTimeRecurringDates>
      )}
    </StyledDateTimeCard>
  );
};

export default DateTimeCard;
